import { useCallback, useEffect, useRef } from 'react';

import { useNavigate, useParams } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';
import { useMarketContext } from '@/pages/Markets/context/MarketContext';
import { getMarkets } from '@/slices/marketsSlice';

const MARKET_FETCH_ERROR_MSG =
  'An error occurred while loading the market page. Please try again later.';

const useMarkets = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();
  const lastMarketId = useRef('');

  const { status: configStatus } = useAppSelector(state => state.userConfig);
  const { activeTab, setActiveTab } = useMarketContext();
  const { showErrorSnackbar } = useSnackbarNotifications();

  const fetchMarkets = useCallback(() => {
    if (params?.marketId && configStatus !== 'loading') {
      const marketId = params.marketId;
      dispatch(getMarkets({ marketId }))
        .unwrap()
        .then(() => {
          lastMarketId.current = marketId;
        })
        .catch(error => {
          console.error(error);
          if (error.status && error.status > 300) {
            showErrorSnackbar(MARKET_FETCH_ERROR_MSG, 5000);
            navigate(-1);
          }
        });
    }
  }, [params.marketId, configStatus, dispatch, showErrorSnackbar, navigate]);

  useEffect(() => {
    const savedTab = sessionStorage.getItem('activeMarketTab');

    if (lastMarketId.current !== params?.marketId) {
      setActiveTab(0);
      sessionStorage.setItem('activeMarketTab', '0');
      fetchMarkets();
    } else if (savedTab !== null) {
      setActiveTab(Number.parseInt(savedTab));
    }
  }, [params?.marketId, setActiveTab, fetchMarkets]);

  useEffect(() => {
    sessionStorage.setItem('activeMarketTab', activeTab.toString());
  }, [activeTab]);
};

export default useMarkets;
